/**
 * ENG-5041: pure helpers for the manager's poll-cycle backoff. Extracted
 * from manager-worker.ts so the math is testable without standing up the
 * whole module (which has hundreds of side-effecting imports).
 */

export const POLL_BACKOFF_BASE_MS = 1_000;
export const POLL_BACKOFF_MAX_MS = 60_000;

/**
 * Compute the next inter-poll delay given how many consecutive failures
 * we've already seen. Doubles per failure (1s → 2s → 4s → 8s → ...),
 * capped at POLL_BACKOFF_MAX_MS. Returns 0 when no failures, so the
 * caller defaults to the normal interval.
 */
export function currentPollBackoffMs(consecutivePollFailures: number): number {
  if (consecutivePollFailures <= 0) return 0;
  const expDelay = POLL_BACKOFF_BASE_MS * Math.pow(2, consecutivePollFailures - 1);
  return Math.min(POLL_BACKOFF_MAX_MS, expDelay);
}

/**
 * Bucket the error message into a stable class string for logging. The
 * point is operators can grep "[poll-backoff] class=5xx" and correlate
 * with API/CW logs without reading the full Error.message.
 *
 * Precedence (CR on PR #982): check 5xx markers BEFORE auth. The CLI's
 * exchange wrapper produces strings like "exchange failed: Internal
 * Server Error", so a naive auth-first match would bury a server-side
 * outage under an 'auth' label — exactly the wrong signal during the
 * 2026-05-12 prod-down. Lowercase first so substring/regex checks
 * don't have to spell every casing the upstream could use.
 */
export function classifyPollError(message: string): 'auth' | '5xx' | 'network' | 'other' {
  const msg = message.toLowerCase();
  if (
    msg.includes('api unreachable') ||
    /\b5\d\d\b/.test(msg) ||
    msg.includes('internal server error') ||
    msg.includes('bad gateway')
  ) return '5xx';
  if (
    msg.includes('exchange failed') ||
    /\b401\b/.test(msg) ||
    msg.includes('invalid token')
  ) return 'auth';
  if (
    msg.includes('econn') ||
    msg.includes('etimedout') ||
    msg.includes('fetch failed')
  ) return 'network';
  return 'other';
}
